import { Alert, Anchor, Badge, Button, Group, Radio, ScrollArea, Select, Stack, Stepper, Table, Text } from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { IconAlertCircle, IconCircleCheck, IconFileSpreadsheet, IconUpload } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { importFields, labels, type Resource } from '@tnn/core'
import { currentLocale } from '@tnn/i18n'
import { ResponsiveDialog } from '@tnn/ui'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ImportResult, queryKeys, resourceApi } from '../api/resources'
import { autoMap, buildRows, downloadTemplate, type ParsedFile, parseFile } from './dataio'

const ACCEPT = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv']
const MAX_ROWS = 5000

/** Upload → match columns → check → import. Nothing is saved until the last step. */
export function ImportWizard({ resource, opened, onClose }: { resource: Resource; opened: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const client = useQueryClient()
  const fields = useMemo(() => importFields(resource), [resource])
  const [step, setStep] = useState(0)
  const [file, setFile] = useState<ParsedFile | null>(null)
  const [fileName, setFileName] = useState('')
  const [mapping, setMapping] = useState<(string | null)[]>([])
  const [mode, setMode] = useState<'create' | 'upsert'>(resource.importKey ? 'upsert' : 'create')
  const [check, setCheck] = useState<ImportResult | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const name = t(labels.plural(resource))
  const ctx = { resource, refs: {}, t, locale: currentLocale(i18n) }

  const rows = useMemo(() => (file ? buildRows(file, mapping, fields) : []), [file, mapping, fields])
  const failedRows = new Set(check?.failed.map((f) => f.row))
  const validRows = rows.filter((_, i) => !failedRows.has(i))

  const reset = () => {
    setStep(0)
    setFile(null)
    setMapping([])
    setCheck(null)
    setResult(null)
    setError(null)
  }

  const close = () => {
    onClose()
    setTimeout(reset, 300)
  }

  const onDrop = async (files: File[]) => {
    const f = files[0]
    if (!f) return
    setError(null)
    try {
      const parsed = await parseFile(f)
      if (parsed.rows.length > MAX_ROWS) parsed.rows = parsed.rows.slice(0, MAX_ROWS)
      setFile(parsed)
      setFileName(f.name)
      setMapping(autoMap(parsed.headers, fields, (field) => t(field.label)))
      setStep(1)
    } catch {
      setError(t('import.unsupported'))
    }
  }

  const runCheck = async () => {
    setBusy(true)
    try {
      setCheck(await resourceApi(resource.key).import({ rows, mode, dryRun: true }))
      setStep(2)
    } finally {
      setBusy(false)
    }
  }

  const runImport = async () => {
    setBusy(true)
    try {
      setResult(await resourceApi(resource.key).import({ rows: validRows, mode }))
      client.invalidateQueries({ queryKey: queryKeys.resource(resource.key) })
      setStep(3)
    } finally {
      setBusy(false)
    }
  }

  const fieldLabel = (fieldName: string | number | undefined) => {
    // A rule may name a field the file cannot fill (a contract driver's partner): its label all the same
    const f = fields.find((x) => x.name === fieldName) ?? resource.field(String(fieldName ?? ''))
    return f ? t(f.label) : String(fieldName ?? '')
  }

  return (
    <ResponsiveDialog opened={opened} onClose={close} title={t('import.title', { name })} size="xl">
      <Stepper active={step} size="sm" mb="lg" allowNextStepsSelect={false}>
        <Stepper.Step label={t('import.stepUpload')} />
        <Stepper.Step label={t('import.stepMap')} />
        <Stepper.Step label={t('import.stepReview')} />
        <Stepper.Step label={t('import.stepDone')} />
      </Stepper>

      {step === 0 && (
        <Stack>
          <Dropzone onDrop={onDrop} accept={ACCEPT} maxFiles={1} radius="lg" p="xl">
            <Stack align="center" gap={6} mih={160} justify="center" style={{ pointerEvents: 'none' }}>
              <IconUpload size={36} stroke={1.5} color="var(--tnn-primary)" />
              <Text size="lg">{t('import.drop')}</Text>
              <Text size="sm" c="dimmed">
                {t('import.dropHint')}
              </Text>
            </Stack>
          </Dropzone>
          {error && (
            <Alert color="danger" icon={<IconAlertCircle size={18} />}>
              {error}
            </Alert>
          )}
          <Anchor component="button" type="button" size="sm" onClick={() => downloadTemplate(resource, ctx, name)}>
            <Group gap={6}>
              <IconFileSpreadsheet size={18} stroke={1.6} />
              {t('import.template')}
            </Group>
          </Anchor>
        </Stack>
      )}

      {step === 1 && file && (
        <Stack>
          <Text size="sm" c="dimmed">
            {fileName} · {t('import.rows', { count: file.rows.length })}
          </Text>
          <ScrollArea.Autosize mah="50dvh">
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('import.column')}</Table.Th>
                  <Table.Th>{t('import.field')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {file.headers.map((h, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: file columns are positional
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {h || `#${i + 1}`}
                      </Text>
                      <Text size="xs" c="dimmed" truncate maw={260}>
                        {String(file.rows[0]?.[i] ?? '')}
                      </Text>
                    </Table.Td>
                    <Table.Td w="50%">
                      <Select
                        size="sm"
                        value={mapping[i] ?? null}
                        placeholder={t('import.ignore')}
                        clearable
                        data={fields.map((f) => ({ value: f.name, label: `${t(f.label)}${f.required ? ' *' : ''}` }))}
                        onChange={(v) => setMapping((m) => m.map((x, j) => (j === i ? v : x === v ? null : x)))}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
          {resource.importKey && (
            <Radio.Group label={t('import.mode')} value={mode} onChange={(v) => setMode(v as 'create' | 'upsert')}>
              <Stack gap="xs" mt="xs">
                <Radio value="upsert" label={t('import.modeUpsert', { key: fieldLabel(resource.importKey) })} />
                <Radio value="create" label={t('import.modeCreate')} />
              </Stack>
            </Radio.Group>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={reset}>
              {t('actions.back')}
            </Button>
            <Button onClick={runCheck} loading={busy} disabled={!mapping.some(Boolean)}>
              {t('actions.next')}
            </Button>
          </Group>
        </Stack>
      )}

      {step === 2 && check && (
        <Stack>
          <Group gap="sm">
            <Badge color="success" size="lg" radius="sm" variant="light">
              {t('import.valid', { count: validRows.length })}
            </Badge>
            {check.failed.length > 0 && (
              <Badge color="danger" size="lg" radius="sm" variant="light">
                {t('import.invalid', { count: check.failed.length })}
              </Badge>
            )}
          </Group>
          {check.failed.length > 0 && (
            <>
              <Text size="sm" c="dimmed">
                {t('import.fixAndRetry')}
              </Text>
              <ScrollArea.Autosize mah="40dvh">
                <Table verticalSpacing="xs" striped>
                  <Table.Tbody>
                    {check.failed.slice(0, 200).flatMap((f) =>
                      f.issues.map((issue, k) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: issues are positional
                        <Table.Tr key={`${f.row}-${k}`}>
                          <Table.Td w={90}>{t('import.row', { row: f.row + 2 })}</Table.Td>
                          <Table.Td w="35%">{fieldLabel(issue.path[0])}</Table.Td>
                          <Table.Td c="var(--tnn-error)">{issue.message}</Table.Td>
                        </Table.Tr>
                      )),
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea.Autosize>
            </>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep(1)}>
              {t('actions.back')}
            </Button>
            <Button onClick={runImport} loading={busy} disabled={validRows.length === 0}>
              {t('import.importValid', { count: validRows.length })}
            </Button>
          </Group>
        </Stack>
      )}

      {step === 3 && result && (
        <Stack align="center" py="lg" gap="xs">
          <IconCircleCheck size={48} stroke={1.5} color="var(--tnn-primary)" />
          <Text size="lg">{t('import.result', { created: result.created, updated: result.updated })}</Text>
          <Button mt="md" onClick={close}>
            {t('actions.done')}
          </Button>
        </Stack>
      )}
    </ResponsiveDialog>
  )
}
